/**
 * The ONE place a user-facing string names the app store.
 *
 * Why it exists (7 Oct 2026): build 2.2.0 (2), the first iOS build that
 * launched, said "Opening Google Play…" on an iPhone and linked
 * "Manage or cancel" to play.google.com. App Review rejects an iOS binary
 * that names another platform's store (guideline 2.3.10), and the manage
 * link was not wrong wording but a dead end on iOS. Every purchase surface
 * now asks this module instead of carrying a literal. Contract test:
 * `billing/__tests__/storeLabels.test.ts` (fails on a "Google Play" literal
 * in any component).
 *
 * Functions take the store as a parameter (defaulting to the running
 * platform) so the mapping is unit-tested without mocking Capacitor.
 */
import { isIOS } from '../native/native';
import { BILLING_FOOTER, STORE_BILLING_LIVE } from './launch';
import { playSubscriptionsUrl } from './revenueCatBilling';

export type AppStore = 'app-store' | 'google-play';

/** The store this build buys through. Web falls back to Google Play — web never sells (WebBillingUnavailableProvider). */
export const currentStore = (): AppStore => (isIOS() ? 'app-store' : 'google-play');

/** "the App Store" / "Google Play" — reads naturally after "Opening", "in", "via". */
export function storeName(store: AppStore = currentStore()): string {
  return store === 'app-store' ? 'the App Store' : 'Google Play';
}

/**
 * Apple's documented manage-subscriptions link ("Handling subscriptions
 * billing", StoreKit docs). Stores do not let an app cancel a subscription;
 * this is where the user does it. The installed RevenueCat plugin (11.3.2)
 * has no native manage sheet, so a link it is — see the 7 Oct note in
 * docs/APPLE_LAUNCH_PLAN.md about mentioning this to App Review.
 */
export const APPLE_SUBSCRIPTIONS_URL = 'https://apps.apple.com/account/subscriptions';

/** Where "Manage or cancel" goes, per store. `sku` only matters on Play. */
export function manageSubscriptionsUrl(sku?: string, store: AppStore = currentStore()): string {
  return store === 'app-store' ? APPLE_SUBSCRIPTIONS_URL : playSubscriptionsUrl(sku);
}

/** Settings footer — follows the launch flag (seam 6) AND the platform. */
export function billingFooter(store: AppStore = currentStore()): string {
  return STORE_BILLING_LIVE ? `Billing via ${storeName(store)}` : BILLING_FOOTER;
}
